import React, { useEffect } from "react";
import AppContainer from "@src/common/app_container";

import { Document, BusinessProfile } from "@src/model/business_profile";
import Alert, { AlertProps } from "@src/common/alert";
import DataTable from "react-data-table-component";
import { useBusinessProfileAPI } from "@src/api/business_profile_api";
import { useAuth0 } from "@auth0/auth0-react";

export default function PersonaDocumentIndex() {
  const [data, setData] = React.useState<Document[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [status, setStatus] = React.useState<AlertProps | null>(null);

  // Upload state
  const [isUploadModalOpen, setIsUploadModalOpen] = React.useState(false);
  const [businessProfiles, setBusinessProfiles] = React.useState<BusinessProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = React.useState<string>("");
  const [fileToUpload, setFileToUpload] = React.useState<File | null>(null);
  const [usageForUpload, setUsageForUpload] = React.useState<string>("PERSONA");
  const [isSaving, setIsSaving] = React.useState(false);

  const [isViewModalOpen, setIsViewModalOpen] = React.useState(false);
  const [selectedDocument, setSelectedDocument] = React.useState<Document | null>(null);
  const [documentContent, setDocumentContent] = React.useState<string>("");

  const businessProfileAPI = useBusinessProfileAPI();
  const { getAccessTokenSilently } = useAuth0();

  // TODO: move this

  const handleUploadDocument = async () => {
    if (!selectedProfileId) {
      setStatus({ type: "error", message: "Please select a business profile." });
      return;
    }
    if (!fileToUpload) {
      setStatus({ type: "error", message: "Please select a file." });
      return;
    }

    setIsSaving(true);
    setStatus(null);

    try {
      const accessToken = await getAccessTokenSilently();
      const formData = new FormData();
      formData.append("file", fileToUpload);
      formData.append("usage", usageForUpload);
      formData.append("businessProfileId", selectedProfileId);

      // Call the new DocumentController endpoint directly
      const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/admin/add`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (response.ok) {
        setStatus({ type: "success", message: "Document uploaded successfully." });
        setIsUploadModalOpen(false);
        setFileToUpload(null);
        setSelectedProfileId("");
        fetchDocuments(); // Refresh list
      } else {
        const text = await response.text();
        throw new Error(text || response.statusText);
      }
    } catch (err: any) {
      setStatus({ type: "error", message: `Error uploading document: ${err.message}` });
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewDocument = (document: Document) => {
    setSelectedDocument(document);
    setIsViewModalOpen(true);

    setDocumentContent(document.content || "");
  };

  const columns = [
    {
      name: "Name",
      selector: (row: Document) => row.name || "",
      sortable: true
    },
    {
      name: "Version",
      selector: (row: Document) => row.version || "",
      sortable: true
    },
    {
      name: "Usage",
      selector: (row: Document) => row.usage || "",
      sortable: true
    },
    {
      name: "Last Updated",
      selector: (row: Document) => row.updated_at || "",
      sortable: true
    },
    {
      name: "Actions",
      cell: (row: Document) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewDocument(row)}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded text-sm"
          >
            View
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded text-sm"
          >
            Delete
          </button>
        </div>
      ),
      width: "300px",
    }
  ];

  const handleDelete = (id?: string) => {
    if (window.confirm("Are you sure you want to delete this document? This action cannot be undone.")) {
      console.log("handleDelete", id);
      // Implementation pending for generic document delete
    }
  };

  const fetchDocuments = () => {
    businessProfileAPI.getAllDocuments().then((documents) => {
      // Filter for relevant types usually used in strategy generation
      setData(documents.filter(doc => ["PERSONA", "UNIVERSAL", "INDUSTRY", "STRATEGY"].includes(doc.usage || "")));
      setLoading(false);
    }).catch((error) => {
      setStatus({ type: "error", message: "Failed to load documents: " + error.message });
      setLoading(false);
    });
  };

  const fetchBusinessProfiles = () => {
    businessProfileAPI.getAllBusinessProfiles().then((profiles) => {
      setBusinessProfiles(profiles);
    }).catch(console.error);
  };

  useEffect(() => {
    fetchDocuments();
    fetchBusinessProfiles();
  }, []);


  return (
    <AppContainer>
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center gap-2 mb-6">
          <h1 className="text-2xl font-bold">Documents</h1>
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex-shrink-0 overflow-clip"
            onClick={() => setIsUploadModalOpen(true)}
          >
            <span className="hidden sm:inline">Add Document</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>

        {status && (
          <div className="mb-4">
            <Alert {...status} onClose={() => setStatus(null)} />
          </div>
        )}

        {/* Upload Modal */}
        {isUploadModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded shadow-xl w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">Upload Document</h2>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Profile</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={selectedProfileId}
                  onChange={(e) => setSelectedProfileId(e.target.value)}
                >
                  <option value="">Select Business Profile...</option>
                  {businessProfiles.map(bp => (
                    <option key={bp.id} value={bp.id}>{bp.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Usage Type</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={usageForUpload}
                  onChange={(e) => setUsageForUpload(e.target.value)}
                >
                  <option value="PERSONA">Persona</option>
                  <option value="UNIVERSAL">Universal</option>
                  <option value="INDUSTRY">Industry</option>
                  <option value="BUSINESS_INTELLIGENCE">Business Intelligence</option>
                  <option value="STRATEGY">Strategy</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">PDF File</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setFileToUpload(e.target.files ? e.target.files[0] : null)}
                  className="w-full"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setIsUploadModalOpen(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-black font-bold py-2 px-4 rounded"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadDocument}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                  disabled={isSaving || !selectedProfileId || !fileToUpload}
                >
                  {isSaving ? "Uploading..." : "Upload"}
                </button>
              </div>
            </div>
          </div>
        )}


        {isViewModalOpen && selectedDocument && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">

              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">{selectedDocument.name}</h2>
                <button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    setSelectedDocument(null);
                    setDocumentContent("");
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                <span className="mr-4">Usage: {selectedDocument.usage}</span>
                <span className="mr-4">Version: {selectedDocument.version}</span>
                {selectedDocument.updated_at && (
                  <span>Last Updated: {new Date(selectedDocument.updated_at).toLocaleString()}</span>
                )}
              </div>

              <div className="p-6 overflow-auto flex-1">

                <div className="prose max-w-none whitespace-pre-wrap">
                  {documentContent || "No content available"}
                </div>

              </div>
            </div>
          </div>
        )}

        <DataTable
          columns={columns}
          data={data}
          pagination
          progressPending={loading}
          progressComponent={<div className="py-6">Loading documents...</div>}
          noDataComponent={<div className="py-6">No documents found</div>}
        />
      </div>
    </AppContainer>
  );
}